import { Component, ElementRef, OnInit, Input, Output, EventEmitter, ContentChildren, QueryList } from '@angular/core';
import { CfCoreComponent } from '../core/core.component';
import { IconModel } from '../../models/icon/icon.model';
import { ButtonModel } from '../../models/button/button.model';
import { TabsModel } from '../../models/tabs/tabs.model';
import { TabsStylingModel } from '../../models/tabs/tabs-styling.model';
import { CfTabsCardComponent } from '../tabs-card/tabs-card.component';
import { TabsTemplates } from '../../templates/tabs.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>Tabs component based on Angular Material Tabs and Angular animations system</p>
 * <p>Example of using:</p>
 * <pre>
 * <code><</code>cf-tabs [properties]="myTabs" [styling]="myTabsStyles"<code>></code><code><</code><code>/</code>cf-tabs<code>></code>
 * </pre>
 */
export class CfTabsComponent extends CfCoreComponent implements OnInit {
/**
 * It is an attribute <b>[properties]</b> of directive <b>cf-tabs</b> which take TabsModel object.
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  activeCardIndex: 3,
 *  showCardNumberAsIcon: true,
 *  showCardNumberAsPrefix: true,
 *  indexBefore: true,
 *  headerPosition: 'right'
 * {{'}'}}</pre></code>
 * <h4>activeCardIndex: number</h4>
 * Active card index
 * <h4>showCardNumberAsIcon: boolean</h4>
 * Showing step number as icon in tabs header steps buttons
 * <h4>showCardNumberAsPrefix: boolean</h4>
 * Showing step prefix as icon in tabs header steps buttons
 * <h4>indexBefore: boolean</h4>
 * Showing header step index (prefix or icon) before step label
 * <h4>headerPosition: string</h4>
 * Header position: above (default), right, below, left
 */
properties: TabsModel;
/**
 * It is an attribute <b>[styling]</b> of directive <b>cf-tabs</b> which take TabsStylingModel object with next properties:
 * <h4>headerSection: StylingModel</h4>
 * Styling properties for tabs header container
 * <h4>container: StylingModel</h4>
 * Styling properties for main tabs container
 */
styling: TabsStylingModel;
/**
 * Active card index
 * @param {?} value
 * @return {?}
 */
set activeCardIndex(value: number) { this.properties.activeCardIndex = value; }
/**
 * If to show step number as icon in Tabs header steps buttons.
 * @param {?} value
 * @return {?}
 */
set showCardNumberAsIcon(value: boolean) { this.properties.showCardNumberAsIcon = value; }
/**
 * If to show step number as prefix in Tabs header steps buttons.
 * @param {?} value
 * @return {?}
 */
set showCardNumberAsPrefix(value: boolean) { this.properties.showCardNumberAsPrefix = value; }
/**
 * If to show step number before the label.
 * @param {?} value
 * @return {?}
 */
set indexBefore(value: boolean) { this.properties.indexBefore = value; }
/**
 * Header position: above (default), right, below, left.
 * @param {?} value
 * @return {?}
 */
set headerPosition(value: string) { this.properties.headerPosition = value; }
/**
 * @hidden 
 * It is an array of tabs cards
 */
tabsCards: QueryList<CfTabsCardComponent>;
/**
 * Output event with active card index
 */
onCardChanged: EventEmitter<CfTabsCardComponent> = new EventEmitter<CfTabsCardComponent>();
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 */
constructor(public elementRef: ElementRef, /**@hidden */ templateService: TemplateService) {
    super(elementRef, templateService);
    this.properties = new TabsModel();
    this.styling = new TabsStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit() {
    this.getMyTemplate('tabs', TabsTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'TABS').then( sampler => {
        // console.log('TABS', this);
      });
    });
  }
/**
 * @hidden 
 * Method to set active card index and emit event with it
 * @param {?} index  active card index
 * @return {?}
 */
_cardChanged(index) {
    if (index !== this.properties.activeCardIndex) {
      this.properties.activeCardIndex = index;
    }
    this.onCardChanged.emit(index);
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-tabs',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"header-{{properties?.headerPosition}} {{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-tabs-container'\"> <md-tab-group class=\"mat-tab-group cf-tabs\" [selectedIndex]=\"properties.activeCardIndex\" (selectedIndexChange)=\"_cardChanged($event)\" [headerPosition]=\"properties.headerPosition\"> <md-tab *ngFor=\"let card of tabsCards; let i = index\" [disabled]=\"card.properties?.disable\" class=\"cf-tabs-card\"> <ng-template md-tab-label> <md-ink-bar class=\"mat-ink-bar card-ink-bar\"></md-ink-bar> <div class=\"{{activeTemplate.style?.headerSection?.class}} {{styling?.headerSection?.class}} {{getDynamicClasses(activeTemplate.style?.headerSection?.dynamicClass)}} {{getDynamicClasses(styling?.headerSection?.dynamicClass)}}\" [ngClass]=\"'cf-card-header'\"> <span *ngIf=\"properties?.showCardNumberAsIcon\" class=\"{{activeTemplate.style?.iconIndex?.class}} {{styling?.iconIndex?.class}} {{getDynamicClasses(activeTemplate.style?.iconIndex?.dynamicClass)}} {{getDynamicClasses(styling?.iconIndex?.dynamicClass)}}\" [ngClass]=\"'cf-card-index-icon'\">{{i + 1}}</span> <span *ngIf=\"properties?.showCardNumberAsPrefix\" class=\"{{activeTemplate.style?.prefixIndex?.class}} {{styling?.prefixIndex?.class}} {{getDynamicClasses(activeTemplate.style?.prefixIndex?.dynamicClass)}} {{getDynamicClasses(styling?.prefixIndex?.dynamicClass)}}\" [ngClass]=\"'cf-card-index-prefix'\">{{i + 1}}</span> <div class=\"{{activeTemplate.style?.label?.class}} {{styling?.label?.class}} {{getDynamicClasses(activeTemplate.style?.label?.dynamicClass)}} {{getDynamicClasses(styling?.label?.dynamicClass)}}\" [ngClass]=\"'cf-card-label'\"> <label>{{card?.properties?.label}}</label> <span><span [ngTemplateOutlet]=\"card.cardHeader\"></span></span> </div> </div> </ng-template> <div class=\"{{card?.activeTemplate?.style?.container?.class}} {{card?.styling?.container?.class}} {{getDynamicClasses(card?.activeTemplate?.style?.container?.dynamicClass)}} {{getDynamicClasses(card?.styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-card-container'\"> <span [ngTemplateOutlet]=\"card.cardContent\"></span> </div>     </md-tab> </md-tab-group> </div>",
  styles: ["/** *How to define a color palette in sass */ .cf-card-header { display: flex; flex-direction: row; align-items: center; justify-content: center; } .cf-card-index-icon { color: white; line-height: 0px; display: inline-flex; width: 24px; height: 24px; align-items: center; justify-content: center; border-radius: 50%; background: #00897B; } .cf-card-index-icon, .cf-card-index-prefix { margin: 0 4px; } :host ::ng-deep .mat-tab-disabled > div > .cf-card-index-icon { background: gray; } :host ::ng-deep > .cf-tabs-container > .cf-tabs .card-ink-bar { display: none; } :host ::ng-deep > .cf-tabs-container > .cf-tabs > .mat-tab-body-wrapper > .mat-tab-body { padding: 8px; } :host ::ng-deep > .cf-tabs-container.header-left, :host ::ng-deep > .cf-tabs-container.header-right { display: flex; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-header, :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-header { height: 100%; border-bottom: none !important; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-header > .mat-tab-label-container, :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-header > .mat-tab-label-container { height: 100%; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-header > .mat-tab-label-container > .mat-tab-list, :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-header > .mat-tab-label-container > .mat-tab-list { height: 100%; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels, :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels { display: flex; flex-direction: column; height: 100%; overflow-y: auto; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels > .mat-tab-label > .cf-card-header, :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels > .mat-tab-label > .cf-card-header { justify-content: flex-start; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels > .mat-tab-label.mat-tab-label-active > .card-ink-bar, :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels > .mat-tab-label.mat-tab-label-active > .card-ink-bar { top: 0; width: 2px; height: auto; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-ink-bar, :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-ink-bar { display: none !important; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-header > .mat-tab-header-pagination, :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-header > .mat-tab-header-pagination { display: none !important; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-body-wrapper, :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-body-wrapper { flex-grow: 1; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs { flex-direction: row; } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-header { border-right: 1px solid rgba(0, 0, 0, 0.12); } :host ::ng-deep > .cf-tabs-container.header-left > .cf-tabs > .mat-tab-header .mat-tab-label-active > .card-ink-bar { left: 0; display: block; } :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs { flex-direction: row-reverse; } :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-header { border-left: 1px solid rgba(0, 0, 0, 0.12); } :host ::ng-deep > .cf-tabs-container.header-right > .cf-tabs > .mat-tab-header .mat-tab-label-active > .card-ink-bar { right: 0; display: block; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'activeCardIndex': [{ type: Input, args: ['activeCardIndex', ] },],
'showCardNumberAsIcon': [{ type: Input, args: ['showCardNumberAsIcon', ] },],
'showCardNumberAsPrefix': [{ type: Input, args: ['showCardNumberAsPrefix', ] },],
'indexBefore': [{ type: Input, args: ['indexBefore', ] },],
'headerPosition': [{ type: Input, args: ['headerPosition', ] },],
'tabsCards': [{ type: ContentChildren, args: [CfTabsCardComponent, ] },],
'onCardChanged': [{ type: Output },],
};
}

function CfTabsComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfTabsComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfTabsComponent.ctorParameters;
/** @type {?} */
CfTabsComponent.propDecorators;
/**
 * It is an attribute <b>[properties]</b> of directive <b>cf-tabs</b> which take TabsModel object.
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  activeCardIndex: 3,
 *  showCardNumberAsIcon: true,
 *  showCardNumberAsPrefix: true,
 *  indexBefore: true,
 *  headerPosition: 'right'
 * {{'}'}}</pre></code>
 * <h4>activeCardIndex: number</h4>
 * Active card index
 * <h4>showCardNumberAsIcon: boolean</h4>
 * Showing step number as icon in tabs header steps buttons
 * <h4>showCardNumberAsPrefix: boolean</h4>
 * Showing step prefix as icon in tabs header steps buttons
 * <h4>indexBefore: boolean</h4>
 * Showing header step index (prefix or icon) before step label
 * <h4>headerPosition: string</h4>
 * Header position: above (default), right, below, left
 * @type {?}
 */
CfTabsComponent.prototype.properties;
/**
 * It is an attribute <b>[styling]</b> of directive <b>cf-tabs</b> which take TabsStylingModel object with next properties:
 * <h4>headerSection: StylingModel</h4>
 * Styling properties for tabs header container
 * <h4>container: StylingModel</h4>
 * Styling properties for main tabs container
 * @type {?}
 */
CfTabsComponent.prototype.styling;
/**
 * @hidden 
 * It is an array of tabs cards
 * @type {?}
 */
CfTabsComponent.prototype.tabsCards;
/**
 * Output event with active card index
 * @type {?}
 */
CfTabsComponent.prototype.onCardChanged;
/** @type {?} */
CfTabsComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
